/**
 * A group subject's weekly slot, and how to turn it into real dates.
 *
 * A slot is a template — "Maths, Mondays 16:00–17:00" — set by an admin on the group's Staff tab
 * and carried on `my_subjects[].time_slot` from `GET tutor/groups`. A class is still stored as an
 * absolute start and end, so scheduling one means projecting the template onto the next few
 * Mondays and letting the tutor pick. That keeps every off-timetable class valid: a subject with no
 * slot simply offers nothing, and the free-form date fields carry on as they always have.
 *
 * `weekday_iso` is 1 = Monday … 7 = Sunday, which is what the server derives from the week_days
 * name. JavaScript's getDay() is 0 = Sunday, hence the conversion in isoDayOf().
 */

const DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

/** "Mon 16:00–17:00", or "" for no slot. */
export function slotLabel(slot) {
  if (!slot) return "";
  const day = DAY_SHORT[slot.weekday_name] || slot.weekday_name || "";
  return `${day} ${slot.start_time}–${slot.end_time}`.trim();
}

/** JS getDay() (0 = Sun) as ISO (1 = Mon … 7 = Sun). */
const isoDayOf = (date) => date.getDay() || 7;

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in local time, which toISOString() will not give. */
export function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function atTime(date, hhmm) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  const out = new Date(date);
  out.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  return out;
}

/**
 * The next few dates this slot falls on, as `datetime-local` values ready for the form.
 *
 * Clamped to the group's run: a slot on a group that ended last term has nothing to offer, and one
 * on a group that has not started yet begins at its start date rather than this week. Occurrences
 * already past today are skipped, so the first chip is always something a tutor can actually run.
 *
 * @param slot   a `time_slot` object, or null
 * @param range  { start_date, end_date } from the group; either may be null for open-ended
 * @param count  how many to return
 * @returns [{ key, date, label, start, end }] — `start`/`end` are datetime-local strings
 */
export function nextOccurrences(slot, range = {}, count = 4) {
  if (!slot?.weekday_iso || !slot.start_time) return [];

  const now = new Date();
  const planStart = range.start_date ? new Date(`${range.start_date}T00:00:00`) : null;
  const planEnd = range.end_date ? new Date(`${range.end_date}T23:59:59`) : null;

  if (planEnd && planEnd < now) return [];

  // Start from today, or the group's start date when it is still in the future.
  const cursor = planStart && planStart > now ? new Date(planStart) : new Date(now);
  cursor.setHours(0, 0, 0, 0);

  // Walk forward to the first matching weekday.
  const delta = (slot.weekday_iso - isoDayOf(cursor) + 7) % 7;
  cursor.setDate(cursor.getDate() + delta);

  const out = [];
  for (let i = 0; i < count + 1 && out.length < count; i += 1) {
    const start = atTime(cursor, slot.start_time);
    const end = slot.end_time ? atTime(cursor, slot.end_time) : null;

    const withinPlan = (!planStart || start >= planStart) && (!planEnd || start <= planEnd);
    if (start > now && withinPlan) {
      out.push({
        key: start.toISOString(),
        date: start,
        label: start.toLocaleDateString('en-GB', { weekday: "short", day: "numeric", month: "short" }),
        start: toLocalInputValue(start),
        end: end ? toLocalInputValue(end) : "",
      });
    }
    cursor.setDate(cursor.getDate() + 7);
  }

  return out;
}

/** The slot a tutor's subject runs in for one group, or null. */
export function slotForSubject(group, subjectId) {
  if (!group || subjectId === "" || subjectId == null) return null;
  const match = (group.my_subjects || []).find((s) => String(s.id) === String(subjectId));
  return match?.time_slot || null;
}

export default nextOccurrences;
